import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { blogApi, BlogPost } from '@/services/apiService';
import { Calendar, ArrowRight } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import CartDrawer from '@/components/CartDrawer';

const BlogPage = () => {
    const { isCartOpen, setIsCartOpen } = useCart();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        blogApi.getAll().then(data => {
            setPosts(data);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, []);

    return (
        <>
            <SEO 
                title="Blog i Poradnik - Włoska Piwnica"
                description="Odkryj tajemnice włoskich win, oliw i przysmaków. Nasz blog to kompendium wiedzy o kulinariach prosto z Włoch."
                canonicalUrl="/blog"
            />
            <div className="min-h-screen flex flex-col">
                <Header onCartClick={() => setIsCartOpen(true)} />

                <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-16">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl mb-12">
                            <h1 className="text-4xl font-display font-bold text-foreground sm:text-5xl mb-4">
                                Włoski Poradnik
                            </h1>
                            <p className="text-xl text-muted-foreground">
                                Odkryj bogactwo smaków, historii i tradycji, które płyną prosto z serca Włoch.
                            </p>
                        </div>

                        {loading ? (
                            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="h-[400px] w-full animate-pulse bg-muted rounded-xl" />
                                ))}
                            </div>
                        ) : posts.length === 0 ? (
                            <div className="py-20 text-center text-muted-foreground">
                                <p className="text-xl">Brak artykułów w tym momencie. Zapraszamy wkrótce!</p>
                            </div>
                        ) : (
                            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                                {posts.map(post => (
                                    <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300 group">
                                        <Link to={`/blog/${post.id}`}>
                                            {post.image ? (
                                                <div className="w-full h-56 overflow-hidden">
                                                    <img 
                                                        src={post.image} 
                                                        alt={post.title} 
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="w-full h-56 bg-secondary/50 flex items-center justify-center">
                                                    <span className="text-muted-foreground opacity-50">Włoska Piwnica</span>
                                                </div>
                                            )}
                                            <CardContent className="p-6">
                                                <div className="flex items-center text-sm text-muted-foreground mb-3">
                                                    <Calendar className="mr-2 h-4 w-4" />
                                                    {new Date(post.createdAt.replace(' ', 'T')).toLocaleDateString('pl-PL')}
                                                </div>
                                                <h2 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                                                    {post.title}
                                                </h2>
                                                <p className="text-muted-foreground line-clamp-3">
                                                    {post.excerpt}
                                                </p>
                                                <div className="mt-5 text-primary font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                                    Czytaj dalej <ArrowRight className="ml-1 h-4 w-4" />
                                                </div>
                                            </CardContent>
                                        </Link>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                </main>

                <Footer />
                <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
            </div>
        </>
    );
};

export default BlogPage;
